/**
 * Bonus-pool distribution.
 *
 * The pool is split between active leaderboard editors in one of two ways
 * (`BonusMode`): in proportion to one metric, or in two parts - one by share of
 * spend, the other by share of output (hook variants launched in the period).
 * Legacy editors' still-running videos are deliberately not in either
 * denominator: they are not being paid, so counting them would silently shrink
 * everyone's share.
 */

export type DistributionMetric = "impressions" | "spend" | "roas";

/**
 * How a period's pool is divided.
 *
 * - `metric`: all of it by share of `DistributionMetric`, the original rule.
 * - `split`: `spendWeight` of it by share of spend, the rest by share of
 *   output. Spend rewards the videos that carry the account; output rewards
 *   shipping, so an editor whose tests have not found spend yet still earns.
 */
export type BonusMode = "metric" | "split";

/**
 * Everything that decides a period's payout, as one value.
 *
 * Snapshotted per pay period (`pay_periods`), so changing the settings never
 * rewrites what a closed period paid.
 */
export interface BonusTerms {
  mode: BonusMode;
  /** Metric mode's divisor. Split mode always uses spend for its spend part. */
  metric: DistributionMetric;
  pool: number;
  /** Editors below this spend get no share of either part. */
  minSpendToQualify: number;
  /** Split mode: the fraction of the pool that follows spend, 0-1. */
  spendWeight: number;
  /** Split mode: a launched variant counts as output once it has spent this much in the period. */
  outputSpendFloor: number;
  /** Split mode: at most this many hook variants of one concept count per period; null for no cap. Unused per concept. */
  outputConceptCap: number | null;
  /** Split mode: what one unit of output is. */
  outputUnit: OutputUnit;
}

/**
 * What one unit of output is.
 *
 * - `variant`: a hook variant - one editor's take on one concept with one
 *   opening (editor + concept + hook + iteration).
 * - `concept`: every hook and iteration of one concept by one editor together,
 *   so ten hooks of one idea count as one.
 *
 * Either way a launch whose name parsed to no concept is its own unit, under
 * its video key.
 */
export type OutputUnit = "variant" | "concept";

/** The weight a new split starts from: half and half, as announced. */
export const DEFAULT_SPEND_WEIGHT = 0.5;

/** The settings fields the bonus terms are read from (`AppSettings`). */
export interface BonusSettings {
  poolAmount: number;
  distributionMetric: DistributionMetric;
  minSpendToQualify: number;
  bonusMode: BonusMode;
  bonusSpendWeight: number;
  outputSpendFloor: number;
  outputConceptCap: number | null;
  outputUnit: OutputUnit;
  bonusModeFrom: string | null;
}

/** The terms the settings describe today. */
export function liveTerms(settings: BonusSettings): BonusTerms {
  return {
    mode: settings.bonusMode,
    metric: settings.distributionMetric,
    pool: settings.poolAmount,
    minSpendToQualify: settings.minSpendToQualify,
    spendWeight: settings.bonusSpendWeight,
    outputSpendFloor: settings.outputSpendFloor,
    outputConceptCap: settings.outputConceptCap,
    outputUnit: settings.outputUnit,
  };
}

/**
 * The terms for a period that has no snapshot of its own.
 *
 * From `bonusModeFrom` on, the settings as they are. Before it, the rule every
 * period was paid on until the split existed: the whole pool by the metric, so
 * a past period never reads as having been paid on terms announced after it.
 */
export function termsByRule(periodStart: string, settings: BonusSettings): BonusTerms {
  const live = liveTerms(settings);
  if (settings.bonusModeFrom === null || periodStart >= settings.bonusModeFrom) return live;
  return { ...live, mode: "metric", spendWeight: 1, outputSpendFloor: 0, outputConceptCap: null, outputUnit: "variant" };
}

/** The metric that orders a board paid on these terms: spend whenever spend is half of the money. */
export function rankMetricOfTerms(terms: Pick<BonusTerms, "mode" | "metric">): RankMetric {
  return terms.mode === "split" ? "spend" : rankMetricOf(terms.metric);
}

/**
 * The metric a board is ordered by, and the headline number on every card.
 *
 * Narrower than `DistributionMetric` because a ratio cannot order a ranking:
 * sorting a hundred creatives by ROAS puts a €12 test that converted once above
 * a €9,000 winner, which is the opposite of what a "top videos" board means.
 * Only the two additive volume metrics can carry a ranking, so ROAS mode splits
 * the pool by return while still ranking on delivery.
 */
export type RankMetric = "impressions" | "spend";

export function rankMetricOf(metric: DistributionMetric): RankMetric {
  return metric === "spend" ? "spend" : "impressions";
}

export interface EditorTotals {
  editorId: number;
  editor: string;
  color: string;
  impressions: number;
  spend: number;
  revenue: number;
  videoCount: number;
  /**
   * Hook variants launched in the range that count as output (`countOutput`):
   * first ever delivery inside it, over the floor, under the cap.
   */
  output: number;
  /** Hook variants launched in the range before the floor and the cap. */
  launched: number;
}

export interface EditorStanding extends EditorTotals {
  /** Revenue ÷ spend, or null when there was no spend to divide by. */
  roas: number | null;
  /** Fraction of the distribution metric, 0–1. */
  share: number;
  /** Share of the pool in currency units, given the metric to date. */
  projectedBonus: number;
  /** Fraction of the qualified field's spend, 0-1. */
  spendShare: number;
  /** Fraction of the qualified field's output, 0-1. */
  outputShare: number;
  /**
   * The two parts of `projectedBonus`. In split mode they add up to it; in
   * metric mode the whole bonus is `spendBonus` when the metric is spend, and
   * both are 0 for impressions and ROAS, which are neither part.
   */
  spendBonus: number;
  outputBonus: number;
  /** False when the editor is below the minimum spend to qualify. */
  qualified: boolean;
  rank: number;
}

/**
 * ROAS is always summed revenue over summed spend, never an average of
 * per-video ratios — an average would weight a €2 test the same as a €5,000
 * winner and would not reconcile with Ads Manager.
 */
export function roasOf(revenue: number, spend: number): number | null {
  return spend > 0 ? revenue / spend : null;
}

/**
 * A retention rate as a fraction of impressions, for hook and hold rate.
 *
 * Null rather than 0 when there were no impressions, so the UI can show a dash
 * instead of implying a real 0%.
 */
export function rateOf(count: number, impressions: number): number | null {
  return impressions > 0 ? count / impressions : null;
}

export function metricValue(totals: EditorTotals, metric: DistributionMetric): number {
  if (metric === "impressions") return totals.impressions;
  if (metric === "spend") return totals.spend;
  return roasOf(totals.revenue, totals.spend) ?? 0;
}

export type StandingsOptions = Pick<
  BonusTerms,
  "metric" | "pool" | "minSpendToQualify"
> &
  Partial<Pick<BonusTerms, "mode" | "spendWeight">>;

/**
 * How the pool divides between the two parts of a split, given what there is
 * to divide by.
 *
 * A part with nothing to divide by - no new launches yet on the first morning
 * of a period, or no spend at all - hands its money to the other part rather
 * than leaving it unpaid: the pool is a promise of a fixed sum, and the two
 * parts are how it is shared, not two separate pots.
 */
export function splitPool(
  pool: number,
  spendWeight: number,
  totals: { spend: number; output: number },
): { spend: number; output: number } {
  const weight = Math.min(Math.max(spendWeight, 0), 1);
  if (totals.spend <= 0 && totals.output <= 0) return { spend: pool * weight, output: pool * (1 - weight) };
  if (totals.output <= 0) return { spend: pool, output: 0 };
  if (totals.spend <= 0) return { spend: 0, output: pool };
  return { spend: pool * weight, output: pool * (1 - weight) };
}

/**
 * Rank editors and split the pool. Disqualified editors keep their stats but
 * take no share, and the pool is redistributed across those who qualify.
 *
 * In split mode `share` is the editor's fraction of the whole pool, so it is
 * still the one number that orders the board and still sums to 1.
 */
export function computeStandings(
  totals: EditorTotals[],
  { metric, pool, minSpendToQualify, mode = "metric", spendWeight = DEFAULT_SPEND_WEIGHT }: StandingsOptions,
): EditorStanding[] {
  const isQualified = (t: EditorTotals) => t.spend >= minSpendToQualify;
  const qualified = totals.filter(isQualified);
  const sum = (pick: (t: EditorTotals) => number) => qualified.reduce((total, t) => total + pick(t), 0);
  const denominator = sum((t) => metricValue(t, metric));
  const spendTotal = sum((t) => t.spend);
  const outputTotal = sum((t) => t.output);
  // The parts as fractions of the pool, so a zero pool still ranks the board.
  const parts = splitPool(1, spendWeight, { spend: spendTotal, output: outputTotal });
  // Separates everyone whose share is 0 - nobody qualified yet, or a whole
  // board with no delivery - by the number the rest of the app ranks on.
  const rank = mode === "split" ? "spend" : rankMetricOf(metric);

  return totals
    .map((t): EditorStanding => {
      const ok = isQualified(t);
      const spendShare = ok && spendTotal > 0 ? t.spend / spendTotal : 0;
      const outputShare = ok && outputTotal > 0 ? t.output / outputTotal : 0;

      let share: number;
      let spendPart: number;
      let outputPart: number;
      if (mode === "split") {
        spendPart = spendShare * parts.spend;
        outputPart = outputShare * parts.output;
        share = spendPart + outputPart;
      } else {
        share = ok && denominator > 0 ? metricValue(t, metric) / denominator : 0;
        spendPart = metric === "spend" ? share : 0;
        outputPart = 0;
      }

      return {
        ...t,
        roas: roasOf(t.revenue, t.spend),
        share,
        projectedBonus: share * pool,
        spendShare,
        outputShare,
        spendBonus: spendPart * pool,
        outputBonus: outputPart * pool,
        qualified: ok,
        rank: 0,
      };
    })
    .sort((a, b) => b.share - a.share || b[rank] - a[rank] || b.output - a.output)
    .map((standing, i) => ({ ...standing, rank: i + 1 }));
}

/* ------------------------------------------------------------------ output */

/**
 * One hook variant whose first ever delivery fell inside the range being
 * counted, as `variantLaunches` reads it.
 */
export interface VariantLaunch {
  editorId: number;
  /** Null when the name parsed to no concept; such a launch is its own concept. */
  conceptId: number | null;
  /** The variant's identity inside the editor (concept/hook/iteration, or the video key). */
  variant: string;
  /** What it spent inside the range. */
  spend: number;
}

/**
 * Output per editor: launched variants, then the floor, then the cap.
 *
 * The floor drops a launch that has not spent `floor` yet; it can still count
 * later in the period once it does. The cap then keeps at most `cap` variants
 * of one concept per editor, the biggest spenders first - which ones is
 * immaterial to the count, but it keeps the list a page might show honest.
 * Variants without a concept are never capped, since there is no concept to
 * cap them by. The same variant listed twice counts once.
 */
export function countOutput(
  launches: VariantLaunch[],
  { floor, cap }: { floor: number; cap: number | null },
): Map<number, { output: number; launched: number }> {
  const seen = new Set<string>();
  const unique = launches.filter((l) => {
    const key = `${l.editorId}|${l.variant}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const result = new Map<number, { output: number; launched: number }>();
  const entry = (editorId: number) => {
    let row = result.get(editorId);
    if (!row) result.set(editorId, (row = { output: 0, launched: 0 }));
    return row;
  };

  const perConcept = new Map<string, number>();
  for (const launch of [...unique].sort((a, b) => b.spend - a.spend)) {
    const row = entry(launch.editorId);
    row.launched += 1;
    if (launch.spend < floor) continue;
    if (cap !== null && launch.conceptId !== null) {
      const key = `${launch.editorId}|${launch.conceptId}`;
      const used = perConcept.get(key) ?? 0;
      if (used >= cap) continue;
      perConcept.set(key, used + 1);
    }
    row.output += 1;
  }
  return result;
}

/**
 * Who leads one side of the split, or null when nobody has anything on it.
 *
 * Ties go to the earlier entry, which is the board's own order.
 */
export function leaderOn<T extends { spend: number; output: number }>(
  standings: T[],
  side: "spend" | "output",
): T | null {
  let best: T | null = null;
  for (const s of standings) if (s[side] > 0 && (best === null || s[side] > best[side])) best = s;
  return best;
}

/** Team-wide totals across the given editors. */
export function teamTotals(
  totals: Array<Pick<EditorTotals, "impressions" | "spend" | "revenue" | "videoCount"> & { output?: number }>,
) {
  const impressions = totals.reduce((s, t) => s + t.impressions, 0);
  const spend = totals.reduce((s, t) => s + t.spend, 0);
  const revenue = totals.reduce((s, t) => s + t.revenue, 0);
  const videoCount = totals.reduce((s, t) => s + t.videoCount, 0);
  const output = totals.reduce((s, t) => s + (t.output ?? 0), 0);
  return { impressions, spend, revenue, videoCount, output, roas: roasOf(revenue, spend) };
}
